import React, { useState } from 'react';
import {SwishFormProps, SwishFormValues} from './types';
import {useTranslation} from 'react-i18next';
import InputText from '@sharedComponents/InputText/InputText';
import Button from '@sharedComponents/Button/Button';
import {ButtonType} from '@sharedComponents/Button/types';
import {FormErrorType} from "@sharedComponents/PantaForm/types";
import Alert from "@sharedComponents/Alert";
import {IndicatorTheme} from '@sharedComponents/LoadingIndicator/types';
import LoadingIndicator from '@sharedComponents/LoadingIndicator';
import {
  validateForm,
  validateOrgNr,
  validateCompanyName,
  validateCity,
  validateStreetAddress,
  validateZipCode,
  validateName,
  validatePhone,
  validateEmail
} from './validation';
import Panel, { PanelType } from '@sharedComponents/Panel/Panel';
import some from 'lodash/some';
import SwishService from '@apps/Shared/Services/Swish';
import { StatusCode } from '@apps/Shared/Services/Response.types';
import { SwishCustomerLookupResponse } from '@apps/Shared/Services/Swish/types';
import Modal from '@sharedComponents/Modal';
import CompanyRegistrationsBlock from '@apps/CompanyRegistrations/CompanyRegistrationsBlock';

const SwishFormBlock = (props: SwishFormProps) => {
  const {t} = useTranslation();
  const hasCompanyRegistrationsProps = !!props.companyRegistrationsProps?.form;

  const [showEntireForm, setShowEntireForm] = useState<boolean>(false);
  const [loadingOrgCheck, setLoadingOrgCheck] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<SwishFormValues>({
    orgNumber: '',
    companyName: '',
    companyStreet: '',
    companyZipcode: '',
    companyCity: '',
    signerName: '',
    signerPhone: '',
    signerEmail: ''
  });

  const [showOrgNotConnected, setShowOrgNotConnected] = useState<boolean>(false);
  const [orgNotConnectedMessage, setOrgNotConnectedMessage] = useState<string>('');

  const [formErrors, setFormErrors] = useState<FormErrorType[]>([]);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormValues({ ...formValues, [id]: value });

    if (id === 'orgNumber') {
      setFormErrors([] as FormErrorType[]);
      setShowConfirmation(false);

      if (showOrgNotConnected) {
        setShowOrgNotConnected(false);
        setOrgNotConnectedMessage('');
      }
      if (showEntireForm) {
        // If org number is changed after full form is shown
        // reset the form and hide it again
        setShowEntireForm(false);
        clearForm();
      }
      return;
    }

    let validationErrors = formErrors;

    switch (id) {
      case 'companyName':
        validationErrors = validateCompanyName(value, validationErrors);
        break;
      case 'companyStreet':
        validationErrors = validateStreetAddress(value, validationErrors);
        break;
      case 'companyZipcode':
        validationErrors = validateZipCode(value, validationErrors);
        break;
      case 'companyCity':
        validationErrors = validateCity(value, validationErrors);
        break;
      case 'signerName':
        validationErrors = validateName(value, validationErrors);
        break;
      case 'signerPhone':
        validationErrors = validatePhone(value, validationErrors);
        break;
      case 'signerEmail':
        validationErrors = validateEmail(value, validationErrors);
        break;
      default:
        break;
    }

    if (validationErrors !== formErrors) {
      setFormErrors(validationErrors);
    }
  };

  const clearForm = () => {
    setFormValues({
      orgNumber: '',
      companyName: '',
      companyStreet: '',
      companyZipcode: '',
      companyCity: '',
      signerName: '',
      signerPhone: '',
      signerEmail: ''
    });
  };

  const checkOrgNumber = async () => {
    setLoadingOrgCheck(true);

    const validateOrgNrResult = validateOrgNr(formValues.orgNumber);
    console.log('Org number validation result:', validateOrgNrResult);

    if (validateOrgNrResult.isValid) {
      const result = await SwishService.GetCustomerInfo(formValues.orgNumber);

      if (result?.status === StatusCode.OK && result?.data) {
        const lookupResult = result.data as SwishCustomerLookupResponse;

        if (lookupResult?.success) {
          assignCompanyDataToForm(lookupResult);
          setShowEntireForm(true);
          setShowOrgNotConnected(false);
          setOrgNotConnectedMessage('');
        } else {
          setShowOrgNotConnected(true);
          setOrgNotConnectedMessage(t('swishform.orgNotConnected')
          );
        }
      } else {
        setFormErrors([{
          key: 'orgNumber',
          messages: [t('error.orgnrServiceUnavailable')]
        }]);
      }
    } else if (validateOrgNrResult.errors.length > 0) {
      // There are validation errors
      setFormErrors(validateOrgNrResult.errors);
    } else {
      // Org number is valid but not connected
      setShowOrgNotConnected(true);
    }

    setLoadingOrgCheck(false);
  };

  const assignCompanyDataToForm = (lookupResult: SwishCustomerLookupResponse) => {
    setFormValues({
      ...formValues,
      companyName: lookupResult.companyName || '',
      companyStreet: lookupResult.postalAddress?.street || '',
      companyZipcode: lookupResult.postalAddress?.postalCode || '',
      companyCity: lookupResult.postalAddress?.city || '',
      signerName: '',
      signerPhone: '',
      signerEmail: ''
    });
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const validationErrors = validateForm(formValues, formErrors);

    if (validationErrors.length > 0) {
      setFormErrors(validationErrors);
    } else {
      setSubmitting(true);
      setSubmitErrors([]);
      setShowConfirmation(false);
      SwishService.Apply(formValues)
        .then(result => {
          if (result.status === StatusCode.OK || result.status === StatusCode.NO_CONTENT) {
            setSubmitErrors([]);
            setShowConfirmation(true);
            setShowEntireForm(false);
            setShowOrgNotConnected(false);
            setFormErrors([]);
            clearForm();
          } else if (result.status === StatusCode.BAD_REQUEST) {
            const errors = result?.data;
            console.log(errors);
            setSubmitErrors(Array.isArray(errors) ? errors : [errors]);
            setShowConfirmation(false);
          } else {
            const errorMessages = result?.data?.message || t('error.unspecified');
            console.log(errorMessages);
            setSubmitErrors(Array.isArray(errorMessages) ? errorMessages : [errorMessages]);
            setShowConfirmation(false);
          }
          setSubmitting(false);
        })
        .catch(error => {
          setSubmitting(false);
          console.log(error);
          setSubmitErrors([t('error.unspecified')]);
          setShowConfirmation(false);
        });
    }
  };

  const uniqueErrors = (key: string) => {
    const errors = formErrors.find(err => err.key === key);
    const uniqueErr = errors ? [...new Set(errors.messages)] : [];

    return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
  };

  return (
    <div className="swish-form-block">
      {props.heading && <h2>{props.heading}</h2>}
      <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>
      <form onSubmit={e => e.preventDefault()}>
        <InputText
          id="orgNumber"
          label={t("swishform.orgnumber")}
          value={formValues.orgNumber}
          required={true}
          hasError={!!some(formErrors, ['key', 'orgNumber'])}
          onChange={handleChange}
          errorText={uniqueErrors('orgNumber')}
        />
        {!showEntireForm && (
          <div className="button-wrapper">
            <button
              type="button"
              className="button button--primary"
              onClick={checkOrgNumber}
              >
                <div className="button__content">
                  {t("swishform.getCompanyInfo")}
                </div>
              </button>
          </div>
        )}
        {loadingOrgCheck && (
          <LoadingIndicator
            text={t('swishform.controllingOrgNumber')}
            customMargins="1rem 0"
            theme={IndicatorTheme.Default}
          />
        )}
        {showEntireForm && (
          <>
            <InputText 
              id="companyName"
              label={t("swishform.companyName")}
              value={formValues.companyName}
              required={true}
              hasError={!!some(formErrors, ['key', 'companyName'])}
              onChange={handleChange}
              errorText={uniqueErrors('companyName')}
            />
            <fieldset>
              <legend>{t("swishform.companyAddressInfo")}</legend>
              <InputText
                id="companyStreet"
                label={t("swishform.companyStreet")}
                value={formValues.companyStreet}
                required={true}
                hasError={!!some(formErrors, ['key', 'companyStreet'])}
                onChange={handleChange}
                errorText={uniqueErrors('companyStreet')}
              />
              <InputText
                id="companyZipcode"
                label={t("swishform.companyZipcode")}
                value={formValues.companyZipcode}
                required={true}
                hasError={!!some(formErrors, ['key', 'companyZipcode'])}
                onChange={handleChange}
                errorText={uniqueErrors('companyZipcode')}
              />
              <InputText
                id="companyCity"
                label={t("swishform.companyCity")}
                value={formValues.companyCity}
                required={true}
                hasError={!!some(formErrors, ['key', 'companyCity'])}
                onChange={handleChange}
                errorText={uniqueErrors('companyCity')}
              />
            </fieldset>
            <fieldset>
              <legend>{t("swishform.signerInfo")}</legend>
              <InputText
                id="signerName"
                label={t("swishform.signerName")}
                value={formValues.signerName}
                required={true}
                hasError={!!some(formErrors, ['key', 'signerName'])}
                onChange={handleChange}
                errorText={uniqueErrors('signerName')}
              />
              <InputText
                id="signerPhone"
                label={t("swishform.signerPhone")}
                value={formValues.signerPhone}
                required={true}
                hasError={!!some(formErrors, ['key', 'signerPhone'])}
                onChange={handleChange}
                errorText={uniqueErrors('signerPhone')}
              />
              <InputText
                id="signerEmail"
                label={t("swishform.signerEmail")}
                value={formValues.signerEmail}
                required={true}
                hasError={!!some(formErrors, ['key', 'signerEmail'])}
                onChange={handleChange}
                errorText={uniqueErrors('signerEmail')}
              />
            </fieldset>
            <div className="button-wrapper">
              <Button
                text={t("button.submit")}
                type={ButtonType.PRIMARY}
                onClick={handleSubmit}
                disabled={submitting}
              />
            </div>
            {submitErrors.length > 0 && (
              <div className={'swish-form-block__error-messages'}>
                <Alert
                    type="danger"
                    text={t('error.somethingwrong')}
                    subtext={submitErrors}
                    compact={false}
                />
              </div>
            )}
          </>
        )}
      </form>
      <Modal open={showConfirmation} handleClose={() => setShowConfirmation(false)}>
        <div className={'swish-form-block__confirmation'}>
          <h2>{t('swishform.submitSuccessTitle')}</h2>
          <p>{t('swishform.submitSuccessBody')}</p>
          <Button
            text={t('button.close')}
            type={ButtonType.PRIMARY}
            onClick={() => setShowConfirmation(false)}
          />
        </div>
      </Modal>
      {showOrgNotConnected && (
        <div className="swish-form-block__not-connected-info">
          <Panel type={PanelType.Primary} compact={true}>
            <p>{orgNotConnectedMessage || t("swishform.orgNotConnected")}</p>
            <div className="swish-form-block__apply-button">
              {hasCompanyRegistrationsProps ? (
                <CompanyRegistrationsBlock
                  {...props.companyRegistrationsProps}
                  buttonType={ButtonType.PRIMARY_ALT}
                />
              ) : (
                <CompanyRegistrationsBlock
                  form="store"
                  buttonText={t("swishform.apply")}
                  buttonType={ButtonType.PRIMARY_ALT}
                  helpText=""
                  termsAndConditions=""
                  customTermsAndConditions=""
                  extra1=""
                  extra2=""
                  extra3=""
                  extra4=""
                  extra5=""
                />
              )}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
};
export default SwishFormBlock;
